import React, { useState, useEffect, useCallback } from 'react';
import { Channel, ChannelType, CreateChannelRequest, UpdateChannelRequest, EmailAuthMode, EmailOAuthProvider } from './types';
import { channelTestApi, oauthApi } from './services/communicationsApi';
import { isOAuthAuthMode, isOAuthAuthorized, resolveEmailAuthMode, setOAuthReturnIntent } from './utils/emailChannel';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { useDirtyChangeReport } from '../../../hooks/useDirtyChangeReport';
import '../../../components/common/CRMForm.css';

interface ChannelSettingsFormProps {
  channel: Channel | null;
  oauthMessage?: { type: 'success' | 'error'; text: string } | null;
  onOauthMessageDismiss?: () => void;
  onSave: (data: CreateChannelRequest | UpdateChannelRequest) => Promise<void>;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onChannelRefresh?: () => Promise<void>;
}

const DEFAULT_PORTS: Record<ChannelType, number> = {
  email: 993,
  sms: 0,
  chat: 0,
  ai: 0
};

const EMAIL_AUTH_DEFAULTS: Record<EmailAuthMode, {
  host: string;
  port: string;
  secure: boolean;
  inboxMailbox: string;
  trashMailbox: string;
  junkMailbox: string;
}> = {
  imap: {
    host: '',
    port: '993',
    secure: true,
    inboxMailbox: 'INBOX',
    trashMailbox: 'TRASH',
    junkMailbox: 'JUNK'
  },
  google: {
    host: 'imap.gmail.com',
    port: '993',
    secure: true,
    inboxMailbox: 'INBOX',
    trashMailbox: '[Gmail]/Trash',
    junkMailbox: '[Gmail]/Spam'
  },
  microsoft: {
    host: 'outlook.office365.com',
    port: '993',
    secure: true,
    inboxMailbox: 'INBOX',
    trashMailbox: 'Deleted Items',
    junkMailbox: 'Junk Email'
  }
};

function authModeFromChannel(channel: Channel | null): EmailAuthMode {
  return resolveEmailAuthMode(channel);
}

const ChannelSettingsForm: React.FC<ChannelSettingsFormProps> = ({
  channel,
  oauthMessage,
  onOauthMessageDismiss,
  onSave,
  onCancel,
  onDirtyChange,
  onChannelRefresh
}) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<ChannelType>('email');
  const [host, setHost] = useState('');
  const [port, setPort] = useState<string>('993');
  const [secure, setSecure] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [inboxMailbox, setInboxMailbox] = useState('INBOX');
  const [trashMailbox, setTrashMailbox] = useState('TRASH');
  const [junkMailbox, setJunkMailbox] = useState('JUNK');
  const [emailAuthMode, setEmailAuthMode] = useState<EmailAuthMode>('imap');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthSetup, setOauthSetup] = useState<{ redirectUri: string; javascriptOrigin: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const { confirmNavigation } = useUnsavedChanges({ hasChanges });
  useDirtyChangeReport(hasChanges, onDirtyChange);

  useEffect(() => {
    if (oauthMessage) {
      if (oauthMessage.type === 'error') {
        setOauthError(oauthMessage.text);
      } else {
        setOauthError(null);
        setTestResult({ success: true, message: oauthMessage.text });
      }
      onOauthMessageDismiss?.();
    }
  }, [oauthMessage, onOauthMessageDismiss]);

  useEffect(() => {
    if (type !== 'email' || emailAuthMode === 'imap') return;
    oauthApi.getConfig(emailAuthMode)
      .then(cfg => setOauthSetup({ redirectUri: cfg.redirectUri, javascriptOrigin: cfg.javascriptOrigin }))
      .catch(() => setOauthSetup(null));
  }, [type, emailAuthMode]);

  const handleAuthorizeOAuth = useCallback(async (provider: EmailOAuthProvider) => {
    if (!channel?.id || !provider) return;
    setOauthError(null);
    setOauthLoading(true);
    setOAuthReturnIntent('settings');
    try {
      const { url } = await oauthApi.getAuthorizeUrl(channel.id, provider);
      window.location.href = url;
    } catch (err: any) {
      setOauthError(err?.response?.data?.error || 'Failed to start authorization.');
      setOauthLoading(false);
    }
  }, [channel]);

  const handleDisconnectOAuth = useCallback(async () => {
    if (!channel?.id) return;
    setOauthError(null);
    try {
      await oauthApi.disconnect(channel.id);
      await onChannelRefresh?.();
      setTestResult({ success: true, message: 'OAuth disconnected.' });
    } catch {
      setOauthError('Failed to disconnect.');
    }
  }, [channel, onChannelRefresh]);

  useEffect(() => {
    if (channel) {
      setName(channel.name);
      setType(channel.type);
      setHost(channel.host || '');
      setPort(channel.port ? String(channel.port) : String(DEFAULT_PORTS[channel.type] || ''));
      setSecure(channel.secure === 1);
      setUsername(channel.username || '');
      setPassword(channel.password || '');
      setInboxMailbox(channel.inboxMailbox || 'INBOX');
      setTrashMailbox(channel.trashMailbox || 'TRASH');
      setJunkMailbox(channel.junkMailbox || 'JUNK');
      setEmailAuthMode(authModeFromChannel(channel));
    } else {
      setName('');
      setType('email');
      setHost('');
      setPort('993');
      setSecure(true);
      setUsername('');
      setPassword('');
      setInboxMailbox('INBOX');
      setTrashMailbox('TRASH');
      setJunkMailbox('JUNK');
      setEmailAuthMode('imap');
    }
    setHasChanges(false);
    setErrors({});
    setTestResult(null);
  }, [channel]);

  const markChanged = () => setHasChanges(true);

  const handleEmailAuthModeChange = (mode: EmailAuthMode) => {
    setEmailAuthMode(mode);
    const defaults = EMAIL_AUTH_DEFAULTS[mode];
    setHost(defaults.host);
    setPort(defaults.port);
    setSecure(defaults.secure);
    setInboxMailbox(defaults.inboxMailbox);
    setTrashMailbox(defaults.trashMailbox);
    setJunkMailbox(defaults.junkMailbox);
    if (mode === 'imap') {
      setOauthError(null);
      setOauthSetup(null);
    }
    markChanged();
  };

  const handleTypeChange = (newType: ChannelType) => {
    setType(newType);
    if (!port || port === String(DEFAULT_PORTS[type])) {
      setPort(String(DEFAULT_PORTS[newType] || ''));
    }
    markChanged();
  };

  const handleTest = async () => {
    if (emailAuthMode === 'imap' && !password.trim()) {
      setTestResult({ success: false, message: 'Enter your password to test the connection.' });
      return;
    }
    setTestResult(null);
    setTesting(true);
    try {
      const result = await channelTestApi.test({
        host: host.trim(),
        port: port ? parseInt(port, 10) : undefined,
        secure: secure ? 1 : 0,
        username: username.trim(),
        password: password || undefined
      });
      setTestResult(result);
    } catch {
      setTestResult({ success: false, message: 'Request failed. Check network or server.' });
    } finally {
      setTesting(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Channel name is required';
    if (type === 'email' && emailAuthMode === 'imap') {
      if (!host.trim()) newErrors.host = 'Host is required for email channels';
      if (!username.trim()) newErrors.username = 'Username is required for email channels';
    } else if (type === 'email' && emailAuthMode !== 'imap') {
      if (!host.trim()) newErrors.host = 'Host is required for email channels';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const data = {
        ...(channel ? { id: channel.id } : {}),
        name: name.trim(),
        type,
        host: host.trim() || undefined,
        port: port ? parseInt(port, 10) : undefined,
        secure: secure ? 1 : 0,
        username: username.trim() || undefined,
        password: password || undefined,
        oauthProvider: type === 'email' && emailAuthMode !== 'imap' ? emailAuthMode : null,
        inboxMailbox: inboxMailbox.trim() || 'INBOX',
        trashMailbox: trashMailbox.trim() || 'TRASH',
        junkMailbox: junkMailbox.trim() || 'JUNK'
      };
      await onSave(data as CreateChannelRequest | UpdateChannelRequest);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges && !confirmNavigation()) return;
    onCancel();
  };

  const isEditing = !!channel;
  const savedChannelId = channel?.id;
  const showOAuthPanel = !!savedChannelId && isOAuthAuthMode(emailAuthMode);
  const showOAuthTest = isEditing && isOAuthAuthMode(emailAuthMode) && !!channel && isOAuthAuthorized(channel);

  return (
    <div className="form">
      <div className="form-header">
        <h2>{isEditing ? 'Edit Channel' : 'Add Channel'}</h2>
        {hasChanges && <span className="form-unsaved-indicator">Unsaved changes</span>}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-content">
          <section className="form-section">
            <h3>Channel Settings</h3>
            <div className="form-section-content">
              <div className="app-form-group">
                <label className="app-form-label required">Channel Name</label>
                <input
                  className={`app-form-input${errors.name ? ' error' : ''}`}
                  type="text"
                  value={name}
                  onChange={e => { setName(e.target.value); markChanged(); }}
                  placeholder="e.g. Gmail, Work Email"
                  maxLength={200}
                />
                {errors.name && <div className="app-form-error">{errors.name}</div>}
              </div>

              <div className="app-form-group">
                <label className="app-form-label required">Type</label>
                <select
                  className="app-form-select"
                  value={type}
                  onChange={e => handleTypeChange(e.target.value as ChannelType)}
                >
                  <option value="email">Email</option>
                  <option value="ai">AI (Smart Inbox)</option>
                  <option value="sms">SMS</option>
                  <option value="chat">Chat</option>
                </select>
              </div>
            </div>
          </section>

          {type === 'ai' && (
            <section className="channel-settings-section">
              <p className="channel-settings-description">
                AI channels organize messages from your other active channels into smart inboxes.
                Use the dedicated AI settings page after creating the channel for future options.
              </p>
            </section>
          )}

          {type === 'email' && (
            <section className="form-section">
              <h3>IMAP Configuration</h3>
              <div className="form-section-content">
                <div className="app-form-group">
                  <label className="app-form-label required">Connection</label>
                  <select
                    className="app-form-select"
                    value={emailAuthMode}
                    onChange={e => handleEmailAuthModeChange(e.target.value as EmailAuthMode)}
                  >
                    <option value="imap">Generic IMAP (username / password)</option>
                    <option value="google">Gmail / Google Workspace (OAuth)</option>
                    <option value="microsoft">Microsoft 365 (OAuth)</option>
                  </select>
                </div>

                {showOAuthPanel && (
                  <div className="channel-oauth-panel">
                    <div className="channel-oauth-header">
                      <span className="channel-oauth-label">
                        {emailAuthMode === 'microsoft' ? 'Microsoft 365' : 'Gmail / Google Workspace'}
                      </span>
                      {channel && isOAuthAuthorized(channel) ? (
                        <span className="channel-oauth-connected">
                          ✓ Connected{channel.oauthEmail ? <> as <strong>{channel.oauthEmail}</strong></> : null}
                        </span>
                      ) : (
                        <span className="channel-oauth-disconnected">Not authorized</span>
                      )}
                    </div>
                    <div className="channel-oauth-actions">
                      {channel && isOAuthAuthorized(channel) ? (
                        <>
                          <button
                            type="button"
                            className="app-btn app-btn-secondary channel-oauth-btn"
                            onClick={() => handleAuthorizeOAuth(emailAuthMode)}
                            disabled={oauthLoading}
                          >
                            {oauthLoading ? 'Redirecting…' : 'Re-authorize'}
                          </button>
                          <button
                            type="button"
                            className="app-btn app-btn-danger channel-oauth-btn"
                            onClick={handleDisconnectOAuth}
                          >
                            Disconnect
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="app-btn channel-oauth-google-btn"
                          onClick={() => handleAuthorizeOAuth(emailAuthMode)}
                          disabled={oauthLoading}
                        >
                          <span className="channel-oauth-google-icon" aria-hidden="true">
                            {emailAuthMode === 'microsoft' ? 'M' : 'G'}
                          </span>
                          {oauthLoading
                            ? 'Redirecting…'
                            : emailAuthMode === 'microsoft'
                              ? 'Authorize with Microsoft'
                              : 'Authorize with Google'}
                        </button>
                      )}
                    </div>
                    {oauthError && (
                      <div className="channel-test-result failure">{oauthError}</div>
                    )}
                    <p className="channel-oauth-note">
                      Uses OAuth2 — no password required. You will be redirected to{' '}
                      {emailAuthMode === 'microsoft' ? 'Microsoft' : 'Google'} to authorize access.
                    </p>
                    {oauthSetup && (
                      <div className="channel-settings-hint">
                        <strong>{emailAuthMode === 'microsoft' ? 'Azure App Registration' : 'Google Cloud Console'}</strong> must include:
                        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                          <li>Authorized redirect URI: <code>{oauthSetup.redirectUri}</code></li>
                          <li>Authorized JavaScript origin: <code>{oauthSetup.javascriptOrigin}</code></li>
                          {emailAuthMode === 'google' && (
                            <li>Your Google account added as a <strong>test user</strong> (Gmail IMAP is a restricted scope)</li>
                          )}
                          {emailAuthMode === 'microsoft' && (
                            <li>Delegated permissions for Office 365 IMAP and SMTP access</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {!savedChannelId && isOAuthAuthMode(emailAuthMode) && (
                  <div className="channel-settings-hint">
                    Save the channel first, then use <strong>Authorize with {emailAuthMode === 'microsoft' ? 'Microsoft' : 'Google'}</strong> — no password needed.
                  </div>
                )}

                <div className="app-form-group">
                  <label className="app-form-label required">Host</label>
                  <input
                    className={`app-form-input${errors.host ? ' error' : ''}`}
                    type="text"
                    value={host}
                    onChange={e => { setHost(e.target.value); markChanged(); }}
                    placeholder="e.g. outlook.office365.com or imap.secureserver.net"
                    maxLength={500}
                  />
                  {errors.host && <div className="app-form-error">{errors.host}</div>}
                </div>

                <div className="app-form-row">
                  <div className="app-form-group">
                    <label className="app-form-label">Port</label>
                    <input
                      className="app-form-input"
                      type="number"
                      value={port}
                      onChange={e => { setPort(e.target.value); markChanged(); }}
                      placeholder="993"
                      min={1}
                      max={65535}
                    />
                  </div>
                  <div className="app-form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.25rem' }}>
                    <label className="app-checkbox-label">
                      <input
                        type="checkbox"
                        checked={secure}
                        onChange={e => { setSecure(e.target.checked); markChanged(); }}
                      />
                      Use SSL/TLS
                    </label>
                  </div>
                </div>

                <div className="app-form-group">
                  <label className={`app-form-label${emailAuthMode === 'imap' ? ' required' : ''}`}>Username / Email</label>
                  <input
                    className={`app-form-input${errors.username ? ' error' : ''}`}
                    type="text"
                    value={username}
                    onChange={e => { setUsername(e.target.value); markChanged(); }}
                    placeholder="user@example.com"
                    maxLength={500}
                  />
                  {errors.username && <div className="app-form-error">{errors.username}</div>}
                </div>

                <div className="app-form-group">
                  <label className="app-form-label">Password / App Password</label>
                  <input
                    className="app-form-input"
                    type="password"
                    value={password}
                    onChange={e => { setPassword(e.target.value); markChanged(); }}
                    placeholder={isEditing ? 'Leave blank to keep existing' : 'Enter password'}
                    autoComplete="new-password"
                    disabled={emailAuthMode !== 'imap'}
                  />
                </div>

                <div className="app-form-group">
                  <label className="app-form-label">Primary Inbox Folder</label>
                  <input
                    className="app-form-input"
                    type="text"
                    value={inboxMailbox}
                    onChange={e => { setInboxMailbox(e.target.value); markChanged(); }}
                    placeholder="INBOX"
                    maxLength={500}
                  />
                </div>

                <div className="app-form-row">
                  <div className="app-form-group">
                    <label className="app-form-label">Trash Folder</label>
                    <input
                      className="app-form-input"
                      type="text"
                      value={trashMailbox}
                      onChange={e => { setTrashMailbox(e.target.value); markChanged(); }}
                      placeholder="TRASH"
                      maxLength={500}
                    />
                  </div>
                  <div className="app-form-group">
                    <label className="app-form-label">Junk Folder</label>
                    <input
                      className="app-form-input"
                      type="text"
                      value={junkMailbox}
                      onChange={e => { setJunkMailbox(e.target.value); markChanged(); }}
                      placeholder="JUNK"
                      maxLength={500}
                    />
                  </div>
                </div>

                <div className="channel-test-row">
                  {showOAuthTest ? (
                    <button
                      type="button"
                      className="app-btn app-btn-secondary channel-test-btn"
                      onClick={async () => {
                        setTesting(true);
                        setTestResult(null);
                        try {
                          const r = await channelTestApi.testOAuth(channel.id);
                          setTestResult(r);
                        } catch {
                          setTestResult({ success: false, message: 'Request failed.' });
                        } finally {
                          setTesting(false);
                        }
                      }}
                      disabled={testing}
                    >
                      {testing ? 'Testing…' : 'Test OAuth Connection'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="app-btn app-btn-secondary channel-test-btn"
                      onClick={handleTest}
                      disabled={testing || !host.trim() || !username.trim() || (emailAuthMode === 'imap' && !password.trim())}
                    >
                      {testing ? 'Testing…' : 'Test Connection'}
                    </button>
                  )}
                  {testResult && (
                    <div className={`channel-test-result${testResult.success ? ' success' : ' failure'}`}>
                      {testResult.success ? '✓' : '✗'} {testResult.message}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="app-btn app-btn-secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="app-btn app-btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Channel'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChannelSettingsForm;
